import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { get, isArray, cloneDeep } from 'lodash';
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/theme/duotone-light.css';
import 'codemirror/mode/javascript/javascript.js';
const emptyTransformer = {
  transformer: {
    definition: '$',
    type: 'jsonata',
    contentTypes: {}
  },
  properties: {}
};
const VIEW_MODES = {
  CREATE: 'CREATE',
  EDIT: 'EDIT'
};
export default {
  name: 'ViewConnectorsConsumersTransformersEdit',
  components: {
    codemirror
  },
  data() {
    return {
      showMetaSkeleton: true,
      tabItem: 'tab-metadata',
      selectedAccount: {},
      accounts: [],
      //
      transformerTypes: ['jsonata', 'javascript'],
      transformerModel: cloneDeep(emptyTransformer),
      inputOutputTypes: ['application/json', 'text/plain', 'generic'],
      transformTestInput: '',
      transformTestOutput: '',
      // code-mirror
      cmOptions: {
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: this.cmLineNumbers,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      }
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'isMobile']),
    canSave() {
      const hasOutputType = get(this.transformerModel, 'transformer.contentTypes.output', null);
      const hasName = get(this.transformerModel, 'name', null);
      const hasAccount = get(this.selectedAccount, 'id', null);
      return hasOutputType && hasName && hasAccount;
    },
    viewMode() {
      return this.$route.params.id ? VIEW_MODES.EDIT : VIEW_MODES.CREATE;
    },
    isCreate() {
      return this.viewMode === VIEW_MODES.CREATE;
    },
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    isWide() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
    canRunTransformerTest() {
      return get(this.selectedAccount, 'id') && this.transformTestInput && this.transformerModel.transformer.contentTypes.output;
    },
    tooltipTransformerTest() {
      // undefined when the transformer's tenant is not among the user's accounts, null when the picker is cleared
      if (!get(this.selectedAccount, 'id')) {
        return this.$i18n_t('Connectors.Transformer.testButtonTooltip.accountRequired');
      } else if (!this.transformerModel.transformer.contentTypes.output) {
        return this.$i18n_t('Connectors.Transformer.testButtonTooltip.outputTypeRequired');
      } else if (!this.transformTestInput) {
        return this.$i18n_t('Connectors.Transformer.testButtonTooltip.examplePayloadRequired');
      }
      return undefined;
    }
  },
  watch: {
    '$route.params.id': {
      handler(id) {
        this.loadTransformer(id);
      },
      deep: true,
      immediate: false
    },
    'transformerModel.transformer.type': {
      handler() {
        if (this.transformerModel.id) {
          return;
        }
        if (get(this.transformerModel, 'transformer.type') === 'jsonata') {
          this.transformerModel.transformer.definition = '$';
        } else if (get(this.transformerModel, 'transformer.type') === 'javascript') {
          this.transformerModel.transformer.definition = 'return x;';
        }
      }
    }
  },
  created() {
    if (this.viewMode === VIEW_MODES.CREATE) {
      this.showMetaSkeleton = false;
    }
  },
  mounted() {
    this.fetchAccounts().then(() => {
      if (this.viewMode === VIEW_MODES.EDIT) {
        this.loadTransformer(this.$route.params.id);
      }
    }).catch(error => {
      console.error(error);
    });
  },
  methods: {
    loadTransformer(id) {
      ApiService.consumerTransformersGet(id).then(response => {
        this.transformerModel = response;
        this.selectedAccount = this.accounts.find(item => item.id === this.transformerModel.tenantId);
        this.showMetaSkeleton = false;
      }).catch(error => {
        // clear transformer and selected account
        this.transformerModel = cloneDeep(emptyTransformer);
        this.selectedAccount = {};
        this.$snack(error.data.original.message, 'error');
      });
    },
    onCmFocus(ref) {
      this.refreshForm(ref);
      this.cmLineNumbers = true;
    },
    refreshForm(ref = 'codemirror') {
      setTimeout(() => {
        if (this.$refs[ref]) {
          // required workaround to get the cursor appearing at the right spot
          // https://discuss.codemirror.net/t/codemirror-cursor-is-not-partially-visible/648/6
          this.$refs[ref].codemirror.refresh();
          this.$refs[ref].codemirror.focus();
        }
      }, 50);
    },
    fetchAccounts() {
      return this.$store.dispatch('app/loadAccounts').then(accounts => {
        this.accounts = accounts;

        // if only one account is available, auto select it
        if (this.accounts.length === 1) {
          this.selectedAccount = this.accounts[0];
        }
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
      });
    },
    updateTransformer() {
      let promise = null;
      if (this.transformerModel.id) {
        promise = ApiService.consumerTransformersUpdate(this.transformerModel.id, this.transformerModel);
      } else {
        promise = ApiService.consumerTransformersCreate(this.selectedAccount.id, this.transformerModel);
      }
      promise.then(() => {
        if (this.transformerModel.id) {
          this.$snack(`${this.$i18n_t('Connectors.Transformer.editDialog.transformerUpdated')}`, 'success');
        } else {
          this.$snack(`${this.$i18n_t('Connectors.Transformer.editDialog.transformerCreated')}`, 'success');
        }
        this.$router.push('/connectors/consumers/transformers');
      }).catch(error => {
        this.$snack(error.response.data.message, 'error');
      });
    },
    testTransformer() {
      let value = this.transformTestInput;
      try {
        const parsedValue = JSON.parse(this.transformTestInput);
        if (typeof parsedValue === 'object') {
          value = parsedValue;
        }
      } catch (e) {
        this.transformTestOutput = '';
        return this.$snack(e.message, 'warning');
      }
      return ApiService.consumerTransformersTest({
        value,
        transformer: cloneDeep(this.transformerModel.transformer),
        tenantId: this.selectedAccount.id
      }).then(res => {
        if (res.error) {
          let message;
          if (isArray(res.error.data)) {
            message = get(res, 'error.data[0].message', 'error');
          } else {
            message = get(res, 'error.data.original.message', 'error');
          }
          const type = this.$i18n_t('Error')[res.error.type];
          this.$snack(`${type}: ${message}`, 'error');
        } else {
          this.transformTestOutput = JSON.stringify(res, null, 2);
        }
      }).catch(e => {
        this.transformTestOutput = '';
        this.$snack(e.message, 'error');
      });
    }
  }
};